/* import __COLOCATED_TEMPLATE__ from './plan-card-container.hbs'; */
/* RESPONSIBLE TEAM: team-self-serve */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import {
  FIN_AI_COPILOT_BASE_ID,
  PLAN_DATA,
  PRICING_5_1_PRICING_MODEL,
  PRICING_5_EARLY_STAGE_BUNDLE,
  PRICING_5_X_CORE_ADVANCED_ID,
  PRICING_5_X_CORE_ESSENTIAL_ID,
  PRICING_5_X_CORE_PLANS,
  PROACTIVE_SUPPORT_PLUS_BASE_ID,
} from 'embercom/lib/billing';
import { Metric } from 'embercom/models/data/pricing/metric-types';
import type IntlService from 'embercom/services/intl';

interface Args {
  earlyStageGraduation: any;
  customer: any;
  allFeatures: string[];
  availableEssentialFeatures: string[];
  availableAdvancedFeatures: string[];
  availableExpertFeatures: string[];
  availablePSPFeatures: string[];
}

interface Signature {
  Element: HTMLDivElement;
  Args: Args;
}

const MIN_EARLY_STAGE_SEATS = 6;

export default class PlanCardContainer extends Component<Signature> {
  @service intl!: IntlService;
  @service customerService: any;
  @service purchaseAnalyticsService: any;
  @service notificationsService: any;
  @service appService: any;
  @service router: any;

  @tracked showStayOnEarlyStageModal = false;

  @tracked showFeatureComparisonModal = false;

  get earlyStageGraduation() {
    return this.args.earlyStageGraduation;
  }

  get timeExtension() {
    return this.customerService.customer.graduatingWithLowUsageDiscount;
  }

  get currentStepYearNumber() {
    return this.earlyStageGraduation.currentStepYearNumber;
  }

  get nextStepYearNumber() {
    return this.earlyStageGraduation.nextStepYearNumber;
  }

  get currentDiscountPercentage() {
    return Math.floor(this.earlyStageGraduation.currentStepPercentageDiscount);
  }

  get nextYearDiscountPercentage() {
    return Math.floor(this.earlyStageGraduation.nextStepPercentageDiscount);
  }

  get shouldDisableEarlyStagePlan() {
    return (
      this.timeExtension ||
      (this.earlyStageGraduation.progressionConfirmedByCustomer &&
        this.earlyStageGraduation.isGraduatingToEarlyStage)
    );
  }

  get shouldDisableRecommendedPlan() {
    return (
      this.earlyStageGraduation.progressionConfirmedByCustomer &&
      !this.earlyStageGraduation.isGraduatingToEarlyStage
    );
  }

  get earlyStagePlansPrice() {
    return this.customerService.getPriceFromPlanIds(
      PRICING_5_EARLY_STAGE_BUNDLE.map(Number),
      this.earlyStageGraduation.pricingModelForEarlyStagePlan,
    );
  }

  get defaultPricingModelPrice() {
    return this.customerService.getPriceFromPlanIds(
      PRICING_5_EARLY_STAGE_BUNDLE.map(Number),
      PRICING_5_1_PRICING_MODEL,
    );
  }

  get earlyStageDiscountedPrice() {
    return (
      this.earlyStageAdvancedDiscountedPrice +
      this.proactiveSupportDiscountedPrice +
      this.copilotDiscountedPrice
    );
  }

  get earlyStageAdvancedDiscountedPrice(): number {
    let priceBreakdown = this.earlyStagePriceBreakdown(PRICING_5_X_CORE_ADVANCED_ID);
    let price = priceBreakdown.charges.find(
      (charge: any) => charge.pricing_metric === Metric.core_seat_count,
    ).price;

    return price / 100;
  }

  get proactiveSupportDiscountedPrice() {
    return this.convertedPriceForPlan(PROACTIVE_SUPPORT_PLUS_BASE_ID);
  }

  get copilotDiscountedPrice() {
    return this.convertedPriceForPlan(FIN_AI_COPILOT_BASE_ID);
  }

  get earlyStageFullPrice() {
    return (
      this.earlyStageAdvancedFullPrice + this.proactiveSupportFullPrice + this.copilotFullPrice
    );
  }

  get earlyStageAdvancedFullPrice() {
    return this.defaultAdvancedCorePrice() * this.earlyStageSeatCount;
  }

  get proactiveSupportFullPrice() {
    return this.defaultPriceForPlan(PROACTIVE_SUPPORT_PLUS_BASE_ID);
  }

  get copilotFullPrice() {
    return this.defaultCopilotPrice() * this.earlyStageSeatCount;
  }

  get recommendedCorePlan() {
    let planIds = this.earlyStageGraduation.recommendedPlanIds;
    if (this.shouldDisableRecommendedPlan) {
      planIds = this.earlyStageGraduation.planIds;
    }

    return planIds.find((id: number) => PRICING_5_X_CORE_PLANS.includes(id.toString()));
  }

  get isEssentialPlan() {
    return this.recommendedCorePlan.toString() === PRICING_5_X_CORE_ESSENTIAL_ID;
  }

  get recommendedCorePlanData() {
    return PLAN_DATA[this.recommendedCorePlan];
  }

  get recommendedCorePlanName() {
    return this.recommendedCorePlanData.name;
  }

  get recommendedPricePerSeat() {
    return this.recommendedCorePlanData.pricePerSeat ?? 0;
  }

  get recommendedSeatPrice() {
    return this.recommendedPricePerSeat * this.coreSeatCount;
  }

  get recommendedLightSeatCount() {
    return this.recommendedCorePlanData.liteSeatLimit;
  }

  get coreSeatCount() {
    return (
      this.appService.app.humanAdminsWithCoreSeat.length || this.appService.app.humanAdmins.length
    );
  }

  get earlyStageSeatCount() {
    return Math.max(MIN_EARLY_STAGE_SEATS, this.coreSeatCount);
  }

  get hasCopilotSeats() {
    return this.appService.app.humanAdminsWithCopilotSeat.length > 0;
  }

  earlyStagePriceBreakdown(planId: string) {
    return this.earlyStagePlansPrice.breakdown.find(
      (breakdown: any) => breakdown.plan_id.toString() === planId,
    );
  }

  defaultPriceBreakdown(planId: string) {
    return this.defaultPricingModelPrice.breakdown.find(
      (breakdown: any) => breakdown.plan_id.toString() === planId,
    );
  }

  convertedPriceForPlan(planId: string): number {
    let amount = this.earlyStagePriceBreakdown(planId).plan_starting_price;
    return amount / 100;
  }

  defaultPriceForPlan(planId: string): number {
    let amount = this.defaultPriceBreakdown(planId).plan_starting_price;
    return amount / 100;
  }

  defaultAdvancedCorePrice(): number {
    let priceBreakdown = this.defaultPriceBreakdown(PRICING_5_X_CORE_ADVANCED_ID);
    let perUnitPrice = priceBreakdown.charges.find(
      (charge: any) => charge.pricing_metric === Metric.core_seat_count,
    ).per_unit_price;

    return perUnitPrice / 100;
  }

  defaultCopilotPrice(): number {
    let priceBreakdown = this.defaultPriceBreakdown(FIN_AI_COPILOT_BASE_ID);
    let perUnitPrice = priceBreakdown.charges.find(
      (charge: any) => charge.pricing_metric === Metric.copilot_seat_count,
    ).per_unit_price;

    return perUnitPrice / 100;
  }

  @action
  closeEarlyStageModal() {
    this.showStayOnEarlyStageModal = false;
  }

  @action
  openStayOnEarlyStageModal() {
    this.purchaseAnalyticsService.trackEvent({
      action: 'clicked',
      context: 'early_stage_plan_card',
      place: 'update_subscription',
      object: 'stay_on_early_stage',
    });
    this.showStayOnEarlyStageModal = true;
  }

  @action
  redirectToPlanBuilder() {
    this.purchaseAnalyticsService.trackEvent({
      action: 'clicked',
      context: 'recommended_plan_card',
      place: 'update_subscription',
      object: 'configure_your_new_subscription',
      plan_name: this.recommendedCorePlanName,
    });

    this.router.transitionTo('apps.app.teams-checkout', this.customerService.app.id, {
      queryParams: {
        plan_id: this.recommendedCorePlan,
      },
    });
  }

  get tooltipText() {
    return this.timeExtension
      ? this.intl.t(
          'new-settings.workspace.billing.update-subscription.plan-card.disabled-button-tooltip-with-es-pre-selected',
        )
      : this.intl.t(
          'new-settings.workspace.billing.update-subscription.plan-card.disabled-button-tooltip',
          {
            htmlSafe: true,
            startDate: this.earlyStageGraduation.graduationDate,
            isEarlyStageConfirmed: this.shouldDisableEarlyStagePlan,
          },
        );
  }

  @action
  async saveEarlyStagePlans() {
    let priceSetIdentifier = this.earlyStageGraduation.pricingModelForEarlyStagePlan;
    let planIds = this.earlyStageGraduation.earlyStagePlanIds;

    this.earlyStageGraduation.planIds = planIds;
    this.earlyStageGraduation.priceSetIdentifier = priceSetIdentifier;

    this.purchaseAnalyticsService.trackEvent({
      action: 'clicked',
      context: 'stay_on_early_stage_modal',
      place: 'update_subscription',
      object: 'stay_on_early_stage',
      plan_ids: planIds,
      price_set_identifier: priceSetIdentifier,
    });

    await taskFor(this.saveEarlyStageGraduation).perform();
    this.closeEarlyStageModal();
  }

  @dropTask
  *saveEarlyStageGraduation() {
    try {
      yield this.earlyStageGraduation.save();
      this.notificationsService.notifyConfirmation(
        this.intl.t('billing.summary.current-and-future-price-component.success-saving-plan'),
      );
      this.router.transitionTo('apps.app.billing.summary', this.customerService.app.id, {
        queryParams: {
          esChoiceConfirmation: true,
        },
      });
    } catch (err) {
      this.notificationsService.notifyError(
        this.intl.t('billing.summary.current-and-future-price-component.error-saving-plan'),
      );
    }
  }

  @action
  onFeatureComparisonModalClose() {
    this.showFeatureComparisonModal = false;
  }

  @action
  onReviewFeatures() {
    this.showFeatureComparisonModal = true;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Billing::UpdateEarlyStageSubscription::PlanCardContainer': typeof PlanCardContainer;
    'billing/update-early-stage-subscription/plan-card-container': typeof PlanCardContainer;
  }
}
